import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';

const MessageLogDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('7days'); // '7days', '30days', 'custom'
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('message_log')
        .select('*')
        .order('created_at', { ascending: false });

      const now = new Date();
      
      if (filterType === '7days') {
        const date = new Date();
        date.setDate(now.getDate() - 7);
        date.setHours(0, 0, 0, 0);
        query = query.gte('created_at', date.toISOString());
      } else if (filterType === '30days') {
        const date = new Date();
        date.setDate(now.getDate() - 30);
        date.setHours(0, 0, 0, 0);
        query = query.gte('created_at', date.toISOString());
      } else if (filterType === 'custom') {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        query = query.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
      }

      const { data, error } = await query.limit(2000); // เพิ่ม Limit เพื่อรองรับ Report

      if (error) throw error;
      setLogs(data);
    } catch (error) {
      console.error('Error fetching logs:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]); // โหลดข้อมูลใหม่เมื่อเปลี่ยน Filter Preset หรือ fetchLogs เปลี่ยน

  // คำนวณสถิติและ Insight
  const stats = useMemo(() => {
    if (!logs.length) return { total: 0, users: 0, topWords: [], categories: {}, hourlyCounts: [], maxHourlyCount: 0, peakHour: 0, dailyCounts: {}, sortedDates: [], maxDailyCount: 0 };

    const uniqueUsers = new Set(logs.map(l => l.user_id).filter(Boolean)).size;
    const categories = {};
    const wordCounts = {};
    const hourlyCounts = new Array(24).fill(0);
    const dailyCounts = {};

    // ใช้ Intl.Segmenter สำหรับตัดคำภาษาไทย (ถ้า Browser รองรับ)
    const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter 
      ? new Intl.Segmenter('th', { granularity: 'word' }) 
      : null;

    logs.forEach(log => {
      // นับหมวดหมู่
      const cat = log.category || 'uncategorized';
      categories[cat] = (categories[cat] || 0) + 1;

      // นับช่วงเวลา (Hourly Distribution)
      if (log.created_at) {
        const date = new Date(log.created_at);
        hourlyCounts[date.getHours()]++;
        
        const dateStr = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); // e.g., "01 Jan"
        dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
      }

      // นับคำ (Word Frequency)
      if (log.message) {
        let words = [];
        if (segmenter) {
          const segments = segmenter.segment(log.message);
          for (const { segment, isWordLike } of segments) {
            if (isWordLike) words.push(segment.toLowerCase());
          }
        } else {
          // Fallback สำหรับ Browser เก่า (ตัดด้วยช่องว่าง)
          words = log.message.toLowerCase().split(/\s+/);
        }

        words.forEach(word => {
          if (word.length > 1) { // ไม่นับตัวอักษรเดียว
            wordCounts[word] = (wordCounts[word] || 0) + 1;
          }
        });
      }
    });

    // เรียงลำดับคำที่พบบ่อย 10 อันดับแรก
    const topWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    const maxHourlyCount = Math.max(...hourlyCounts, 1);
    const peakHour = hourlyCounts.indexOf(maxHourlyCount);

    // เรียงลำดับวันที่สำหรับกราฟ Daily Trend
    // (เนื่องจากเราดึงข้อมูลมาแบบ desc วันที่ล่าสุดจะมาก่อน เราต้องกลับด้านให้กราฟเรียงจากอดีต -> ปัจจุบัน)
    const sortedDates = Object.keys(dailyCounts).reverse(); 
    const maxDailyCount = Math.max(...Object.values(dailyCounts), 1);

    return {
      total: logs.length,
      users: uniqueUsers,
      categories,
      topWords,
      hourlyCounts,
      maxHourlyCount,
      peakHour,
      dailyCounts,
      sortedDates,
      maxDailyCount
    };
  }, [logs]);

  return (
    <div style={{ padding: '40px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-primary)', fontWeight: '700', fontSize: '28px' }}>📊 Message Analytics Report</h2>
        
        {/* Filter Controls */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center', backgroundColor: '#fff', padding: '15px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', backgroundColor: 'var(--color-surface-muted)', borderRadius: '8px', padding: '4px' }}>
            <button onClick={() => setFilterType('7days')} style={filterType === '7days' ? activeTabStyle : tabStyle}>Last 7 Days</button>
            <button onClick={() => setFilterType('30days')} style={filterType === '30days' ? activeTabStyle : tabStyle}>Last 30 Days</button>
            <button onClick={() => setFilterType('custom')} style={filterType === 'custom' ? activeTabStyle : tabStyle}>Custom Range</button>
          </div>

          {filterType === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={dateInputStyle} />
              <span style={{ color: 'var(--color-text-subtle)' }}>to</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={dateInputStyle} />
              <button 
                onClick={fetchLogs} 
                disabled={loading}
                style={actionButtonStyle}
              >
                Apply Filter
              </button>
            </div>
          )}

          {filterType !== 'custom' && (
            <button 
              onClick={fetchLogs}
              disabled={loading}
              style={{ ...actionButtonStyle, marginLeft: 'auto' }}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: 'red', marginBottom: '20px', padding: '15px', backgroundColor: '#ffebee', borderRadius: '8px' }}>Error: {error}</div>}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Total Messages</h3>
          <p style={cardValueStyle}>{stats.total}</p>
        </div>
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Active Users</h3>
          <p style={cardValueStyle}>{stats.users}</p>
        </div>
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Top Category</h3>
          <p style={cardValueStyle}>
            {Object.entries(stats.categories).sort((a, b) => b[1] - a[1])[0]?.[0] || '-'}
          </p>
        </div>
      </div>

      {/* Daily Trend Chart (New) */}
      {stats.sortedDates.length > 1 && (
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '24px', border: '1px solid var(--color-border)', marginBottom: '30px' }}>
          <h3 style={{ ...cardTitleStyle, marginBottom: '30px', fontSize: '18px' }}>📈 Daily Message Volume</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', height: '200px', gap: '10px', paddingBottom: '10px', overflowX: 'auto' }}>
            {stats.sortedDates.map((date) => {
              const count = stats.dailyCounts[date];
              return (
                <div key={date} style={{ flex: 1, minWidth: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                  <div 
                    style={{ 
                      width: '100%', 
                      backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', 
                      height: `${(count / stats.maxDailyCount) * 100}%`,
                      borderRadius: '4px 4px 0 0',
                      minHeight: '4px',
                      opacity: 0.8,
                      transition: 'height 0.5s ease'
                    }} 
                    title={`${date}: ${count} messages`}
                  />
                  <span style={{ fontSize: '10px', color: 'var(--color-text-subtle)', marginTop: '8px', whiteSpace: 'nowrap' }}>
                    {date}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Insights Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        {/* Top Words */}
        <div style={{ ...cardStyle, alignItems: 'flex-start' }}>
          <h3 style={{ ...cardTitleStyle, marginBottom: '15px' }}>🔥 Top Keywords</h3>
          <div style={{ width: '100%' }}>
            {stats.topWords.map(([word, count], index) => (
              <div key={word} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', paddingBottom: '8px', borderBottom: index < stats.topWords.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                <span style={{ color: 'var(--color-text)', fontWeight: '500' }}>{index + 1}. {word}</span>
                <span style={{ color: 'var(--color-primary)', fontWeight: 'bold' }}>{count}</span>
              </div>
            ))}
            {stats.topWords.length === 0 && <p style={{ color: 'var(--color-text-subtle)' }}>No data available</p>}
          </div>
        </div>

        {/* Category Distribution */}
        <div style={{ ...cardStyle, alignItems: 'flex-start' }}>
          <h3 style={{ ...cardTitleStyle, marginBottom: '15px' }}>📁 Category Distribution</h3>
          <div style={{ width: '100%' }}>
            {Object.entries(stats.categories).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ flex: 1, marginRight: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>{cat}</span>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>{Math.round((count / stats.total) * 100)}%</span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: 'var(--color-border)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${(count / stats.total) * 100}%`, height: '100%', backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity by Time Chart */}
      <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', padding: '24px', border: '1px solid var(--color-border)' }}>
        <h3 style={{ ...cardTitleStyle, marginBottom: '30px', fontSize: '18px' }}>⏰ Peak Activity Times (24 Hours)</h3>
        
        <div style={{ display: 'flex', alignItems: 'flex-end', height: '200px', gap: '6px', paddingBottom: '10px' }}>
          {stats.hourlyCounts && stats.hourlyCounts.map((count, hour) => (
            <div key={hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
              <div 
                style={{ 
                  width: '100%', 
                  backgroundColor: hour === stats.peakHour ? 'var(--color-primary)' : '#81C784', 
                  height: `${(count / stats.maxHourlyCount) * 100}%`,
                  borderRadius: '4px 4px 0 0',
                  transition: 'all 0.3s ease',
                  minHeight: count > 0 ? '4px' : '0',
                  position: 'relative',
                  cursor: 'pointer'
                }} 
                title={`${hour}:00 - ${hour + 1}:00 : ${count} messages`}
              >
                 {count > 0 && (
                    <span style={{ 
                      position: 'absolute', 
                      top: '-20px', 
                      left: '50%', 
                      transform: 'translateX(-50%)', 
                      fontSize: '10px', 
                      color: 'var(--color-text-muted)',
                      fontWeight: 'bold'
                    }}>
                      {count}
                    </span>
                 )}
              </div>
              <span style={{ fontSize: '10px', color: 'var(--color-text-subtle)', marginTop: '8px' }}>
                {hour}
              </span>
            </div>
          ))}
        </div>
        
        <div style={{ textAlign: 'center', marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-primary-soft)', borderRadius: '8px', color: '#33691E' }}>
            Most active time: <strong>{stats.peakHour}:00 - {stats.peakHour + 1}:00</strong> with <strong>{stats.hourlyCounts ? stats.hourlyCounts[stats.peakHour] : 0}</strong> messages.
        </div>
      </div>
    </div>
  );
};

const cardStyle = {
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: '1px solid var(--color-border)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const cardTitleStyle = {
  margin: '0 0 10px 0',
  fontSize: '14px',
  color: 'var(--color-text-muted)',
  fontWeight: '500'
};

const cardValueStyle = {
  margin: 0,
  fontSize: '28px',
  fontWeight: 'bold',
  color: 'var(--color-primary)'
};

const tabStyle = {
  padding: '8px 16px',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  color: 'var(--color-text-muted)',
  fontWeight: '500',
  borderRadius: '6px',
  transition: 'all 0.2s'
};

const activeTabStyle = {
  ...tabStyle,
  backgroundColor: '#fff',
  color: 'var(--color-primary)',
  fontWeight: 'bold',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const dateInputStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  fontFamily: 'inherit'
};

const actionButtonStyle = {
  padding: '8px 20px',
  backgroundColor: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: '600',
  transition: 'all 0.2s'
};

export default MessageLogDashboard;
